import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, Search, Link, X } from "lucide-react";

export function ImagePicker({ 
  open, 
  onClose, 
  onSelect, 
  images = [], 
  title = "Select an Image" 
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = images.filter(img => {
    const url = typeof img === 'string' ? img : img.url;
    const alt = typeof img === 'string' ? '' : (img.alt || '');
    return url.toLowerCase().includes(searchTerm.toLowerCase()) || 
           alt.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSelect = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
      setSelectedImage(null);
    }
  };

  const handleCustomUrl = () => {
    if (customUrl.trim()) {
      onSelect(customUrl.trim());
      onClose();
      setCustomUrl("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="image-picker-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="image-search-input"
            />
          </div>

          {/* Image Grid */}
          <ScrollArea className="h-[300px]">
            {filteredImages.length > 0 ? (
              <div className="grid grid-cols-3 gap-3 p-1">
                {filteredImages.map((img, idx) => {
                  const url = typeof img === 'string' ? img : img.url;
                  const alt = typeof img === 'string' ? `Image ${idx + 1}` : (img.alt || `Image ${idx + 1}`);
                  const isSelected = selectedImage === url;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(url)}
                      className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected 
                          ? 'border-violet-500 ring-2 ring-violet-200' 
                          : 'border-slate-200 hover:border-violet-300'
                      }`}
                      data-testid={`image-option-${idx}`}
                    >
                      <img 
                        src={url} 
                        alt={alt}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text fill="%2394a3b8" x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="12">No Image</text></svg>';
                        }}
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-violet-500/20 flex items-center justify-center">
                          <div className="bg-violet-500 rounded-full p-1">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <p>No images found</p>
                <p className="text-sm">Add a custom URL below</p>
              </div>
            )}
          </ScrollArea>

          {/* Custom URL Input */}
          <div className="border-t pt-4">
            <p className="text-sm text-slate-500 mb-2">Or add a custom image URL:</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="https://example.com/image.jpg"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  className="pl-10"
                  data-testid="custom-url-input"
                />
              </div>
              <Button 
                onClick={handleCustomUrl}
                disabled={!customUrl.trim()}
                variant="outline"
                data-testid="add-custom-url-btn"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSelect}
              disabled={!selectedImage}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="select-image-btn"
            >
              Select Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImagePicker;
